import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LogoTribe, MarqueTribe } from './charte/LogoTribe';

/* Navigation du portail, d'après les maquettes : barre indigo à gauche,
   logo complet en écru directement sur l'indigo, quatre entrées (Accueil, Missions,
   Documents, Compte), le client et son organisation en pied. « Nouvelle
   mission » n'est pas une entrée : c'est le bouton orange de l'Accueil et
   de Missions. Sous 1024 px, barre du haut et menu repliable (bouton
   « Menu », Échap ou un lien le referment). */

const IconeAccueil = () => (
  <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M3 11.5 12 4l9 7.5V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1z" /></svg>
);
const IconeMissions = () => (
  <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 21s7-6.2 7-11a7 7 0 1 0-14 0c0 4.8 7 11 7 11z" /><circle cx="12" cy="10" r="2.5" /></svg>
);
const IconeDocuments = () => (
  <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M6 3h8l4 4v14H6z" /><path d="M14 3v4h4M9 12h6M9 16h6" /></svg>
);
const IconeCompte = () => (
  <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 4-6 8-6s8 2 8 6" /></svg>
);

export const ENTREES: { libelle: string; chemin: string; icone: React.ReactNode; exact?: boolean }[] = [
  { libelle: 'Accueil',   chemin: '/',          icone: <IconeAccueil />, exact: true },
  { libelle: 'Missions',  chemin: '/missions',  icone: <IconeMissions /> },
  { libelle: 'Documents', chemin: '/documents', icone: <IconeDocuments /> },
  { libelle: 'Compte',    chemin: '/compte',    icone: <IconeCompte /> },
];

/* « Missions » reste allumé sur la création de mission et sur une fiche. */
const estActif = (chemin: string, entree: (typeof ENTREES)[number]) =>
  entree.exact ? chemin === entree.chemin : chemin === entree.chemin || chemin.startsWith(entree.chemin + '/');

const Navigation: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { logout, user } = useAuth();
  const [ouvert, setOuvert] = useState(false);
  const boutonMenu = useRef<HTMLButtonElement>(null);

  // Toute navigation referme le menu repliable.
  useEffect(() => { setOuvert(false); }, [pathname]);

  useEffect(() => {
    if (!ouvert) return;
    const surTouche = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOuvert(false); boutonMenu.current?.focus(); }
    };
    document.addEventListener('keydown', surTouche);
    return () => document.removeEventListener('keydown', surTouche);
  }, [ouvert]);

  const handleLogout = async () => {
    await logout();
    navigate('/connexion');
  };

  return (
    <>
      <header className="barre-haut">
        <LogoTribe largeur={28} surIndigo />
        <button
          ref={boutonMenu}
          type="button"
          className="bouton bouton--secondaire bouton--petit"
          aria-expanded={ouvert}
          aria-controls="menu-principal"
          onClick={() => setOuvert(o => !o)}
        >
          {ouvert ? 'Fermer' : 'Menu'}
        </button>
      </header>

      {ouvert && <div className="menu__voile" onClick={() => setOuvert(false)} aria-hidden />}

      <nav id="menu-principal" className={`menu${ouvert ? ' menu--ouvert' : ''}`} aria-label="Navigation principale">
        <Link to="/" aria-label="Accueil du portail client Tribe" style={{ textDecoration: 'none' }}>
          <MarqueTribe />
        </Link>

        <div className="menu__liens">
          {ENTREES.map(e => (
            <Link
              key={e.chemin}
              to={e.chemin}
              className="menu__lien"
              aria-current={estActif(pathname, e) ? 'page' : undefined}
            >
              {e.icone}
              {e.libelle}
            </Link>
          ))}
        </div>

        <div className="menu__pied">
          {user?.nom && <div className="menu__nom">{user.nom}</div>}
          {user?.organisation && <div className="menu__organisation">{user.organisation}</div>}
          {!user?.nom && user?.email && <div className="menu__organisation">{user.email}</div>}
          <div className="menu__entite">TRIBE SÉNÉGAL · FR</div>
          <div className="menu__liens-pied">
            <button type="button" onClick={handleLogout}>Déconnexion</button>
            <a href="/legal">Mentions légales</a>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navigation;
